import React, { useState } from 'react';
import { Box, Button, Container, TextField, Typography } from '@mui/material';
import axios from 'axios';
import constants from "../utils/constants";
import { useNavigate, useLocation } from 'react-router-dom';
import locales from '../locales.json';
import { useLang } from "../context/LangContext";

const { BACKEND_URL } = constants;

function SignIn({ setIsAuthenticated }) {
  const { lang } = useLang();
  const { signin_page } = locales;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once the server asks for a second factor: { token, method }.
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from || '/';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const completeSignIn = (token) => {
    localStorage.setItem('auth_token', token);
    setError('');
    setIsAuthenticated(true);
    navigate(from);
  };

  const backToCredentials = (message = '') => {
    setMfa(null);
    setCode('');
    setInfo('');
    setFormData((current) => ({ ...current, password: '' }));
    setError(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError(signin_page.all_fields_required[lang]);
      return;
    }

    setLoading(true);
    try {
      const signInResult = await axios.post(
        `${BACKEND_URL}/authentication/signin-web`,
        {
          email: formData.email,
          password: formData.password,
        }
      );
      const { data } = signInResult;
      if (signInResult.status === 200 && data.mfaRequired && data.mfaLoginToken) {
        setMfa({ token: data.mfaLoginToken, method: data.mfaMethod });
        setCode('');
        setError('');
        setInfo('');
      } else if (signInResult.status === 200 && data.token) {
        completeSignIn(data.token);
      } else {
        setError(signin_page.generic_error[lang]);
      }
    } catch (err) {
      const apiError = err.response?.data?.error;
      if (apiError === 'mfa_locked') {
        setError(signin_page.mfa_locked[lang]);
      } else if (apiError === 'mfa_email_failed') {
        setError(signin_page.mfa_email_failed[lang]);
      } else {
        setError(signin_page.invalid_credentials[lang]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    const cleanCode = code.replace(/\s/g, '');
    if (!cleanCode) {
      setError(signin_page.all_fields_required[lang]);
      return;
    }

    setLoading(true);
    setInfo('');
    try {
      const result = await axios.post(
        `${BACKEND_URL}/authentication/mfa/verify-login`,
        { mfaLoginToken: mfa.token, code: cleanCode, clientType: 'web' }
      );
      if (result.data.token) {
        completeSignIn(result.data.token);
      } else {
        setError(signin_page.generic_error[lang]);
      }
    } catch (err) {
      const apiError = err.response?.data?.error;
      if (apiError === 'mfa_locked') {
        backToCredentials(signin_page.mfa_locked[lang]);
      } else if (apiError === 'Invalid or expired session') {
        backToCredentials(signin_page.mfa_session_expired[lang]);
      } else if (err.response?.status === 401) {
        setError(signin_page.mfa_invalid_code[lang]);
      } else {
        setError(signin_page.generic_error[lang]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await axios.post(`${BACKEND_URL}/authentication/mfa/resend-code`, {
        mfaLoginToken: mfa.token,
      });
      setInfo(signin_page.mfa_resend_sent[lang]);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(signin_page.mfa_resend_limit[lang]);
      } else if (err.response?.status === 401) {
        backToCredentials(signin_page.mfa_session_expired[lang]);
      } else {
        setError(signin_page.generic_error[lang]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: '100vh', md: 'calc(100vh - 64px)' }, // Plein écran sur mobile
        px: { xs: 1, sm: 2, md: 0 }, // Padding horizontal responsive
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#f9f9f9',
          padding: { xs: '20px', sm: '32px' }, // Padding adapté
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '400px',
          mx: 'auto',
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ fontSize: { xs: '1.7rem', md: '2rem' } }}>
          {mfa ? signin_page.mfa_title[lang] : signin_page.title[lang]}
        </Typography>
        {mfa && (
          <form onSubmit={handleVerify}>
            <Typography align="center" sx={{ marginTop: '8px' }}>
              {mfa.method === 'totp'
                ? signin_page.mfa_instruction_totp[lang]
                : signin_page.mfa_instruction_email[lang]}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              label={signin_page.mfa_code_label[lang]}
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              variant="outlined"
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', 'aria-label': signin_page.mfa_code_label[lang] }}
            />
            {error && (
              <Typography color="error" sx={{ marginTop: '8px', fontSize: { xs: '0.95rem', md: '1rem' } }}>
                {error}
              </Typography>
            )}
            {info && (
              <Typography color="success.main" sx={{ marginTop: '8px', fontSize: { xs: '0.95rem', md: '1rem' } }}>
                {info}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{
                marginTop: '24px',
                padding: { xs: '10px 0', md: '12px 0' },
                borderRadius: '24px',
                fontSize: { xs: '1rem', md: '1.1rem' },
              }}
            >
              {signin_page.mfa_verify_button[lang]}
            </Button>
            {mfa.method === 'email' && (
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{
                  marginTop: '16px',
                  padding: { xs: '10px 0', md: '12px 0' },
                  borderRadius: '24px',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                }}
                onClick={handleResend}
              >
                {signin_page.mfa_resend[lang]}
              </Button>
            )}
            <Button
              variant="text"
              color="primary"
              fullWidth
              sx={{ marginTop: '16px' }}
              onClick={() => backToCredentials()}
            >
              {signin_page.mfa_back[lang]}
            </Button>
          </form>
        )}
        {!mfa && (
        <>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={signin_page.email_label[lang]}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label={signin_page.password_label[lang]}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
          {error && (
            <Typography color="error" sx={{ marginTop: '8px', fontSize: { xs: '0.95rem', md: '1rem' } }}>
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              marginTop: '24px',
              padding: { xs: '10px 0', md: '12px 0' },
              borderRadius: '24px',
              fontSize: { xs: '1rem', md: '1.1rem' },
            }}
          >
            {signin_page.sign_in_button[lang]}
          </Button>
        </form>

        <Button
          variant="outlined"
          color="primary"
          fullWidth
          sx={{
            marginTop: '16px',
            padding: { xs: '10px 0', md: '12px 0' },
            borderRadius: '24px',
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}
          onClick={() => navigate('/signup')}
        >
          {signin_page.create_account_button[lang]}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          sx={{
            marginTop: '16px',
            padding: { xs: '10px 0', md: '12px 0' },
            borderRadius: '24px',
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}
          onClick={() => navigate('/forgot-password')}
        >
          {signin_page.forgot_password_button[lang]}
        </Button>
        </>
        )}
      </Box>
    </Container>
  );
}

export default SignIn;